/**
 * Persisted per-list view preferences for the shared list detail page: which
 * view is active (Sections / List / Projects / Custom) and the selected Show
 * (assignee) and Review filter chips. A per-user, per-browser, PER-LIST record
 * (localStorage) — the same place the arche keeps its other UI prefs (see
 * collapsedSections / hideEmptySections) — so returning to a list restores how
 * you last looked at it.
 *
 * The transient search query is intentionally NOT persisted (it's a momentary
 * lookup, not a saved view). Hide-empty lives in its own store already.
 *
 * Shape: a { [listId]: Partial<ListViewPrefs> } map. A list the user has never
 * touched is absent → the defaults below. Values are clamped to the known
 * unions on read so a stale/typo'd stored value can never wedge the UI.
 */
import { useCallback, useSyncExternalStore } from 'react';
import type { AssigneeFilterValue } from '../components/AssigneeFilterBar';
import type { ReviewFilterValue } from '../components/ReviewFilterBar';

export type GroupByValue = 'section' | 'list' | 'project' | 'custom';

export interface ListViewPrefs {
  groupBy: GroupByValue;
  /** Show filter: 'all' | 'mine' | 'unassigned' | a principalId. */
  assigneeFilter: AssigneeFilterValue;
  reviewFilter: ReviewFilterValue;
}

const DEFAULTS: ListViewPrefs = {
  groupBy: 'section',
  assigneeFilter: 'all',
  reviewFilter: 'both',
};

const GROUP_BY_VALUES: readonly string[] = ['section', 'list', 'project', 'custom'];
const REVIEW_VALUES: readonly string[] = ['both', 'review', 'regular', 'blocked'];

function clampGroupBy(v: unknown): GroupByValue {
  return typeof v === 'string' && GROUP_BY_VALUES.includes(v)
    ? (v as GroupByValue)
    : DEFAULTS.groupBy;
}
function clampReview(v: unknown): ReviewFilterValue {
  return typeof v === 'string' && REVIEW_VALUES.includes(v)
    ? (v as ReviewFilterValue)
    : DEFAULTS.reviewFilter;
}

const STORAGE_KEY = 'todo-lists:view-prefs:v1';

type StoredMap = Record<string, Partial<ListViewPrefs>>;

function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function readFromStorage(): StoredMap {
  const storage = safeLocalStorage();
  if (!storage) return {};
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as StoredMap;
  } catch {
    return {};
  }
}

function writeToStorage(map: StoredMap): void {
  const storage = safeLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // quota / private-mode — persistence degrades to in-session only
  }
}

let snapshot: StoredMap | null = null;
const listeners = new Set<() => void>();

function getSnapshot(): StoredMap {
  if (snapshot === null) snapshot = readFromStorage();
  return snapshot;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function commit(next: StoredMap): void {
  snapshot = next;
  writeToStorage(next);
  listeners.forEach((l) => l());
}

export interface ListViewPrefsHandle extends ListViewPrefs {
  setGroupBy: (v: GroupByValue) => void;
  setAssigneeFilter: (v: AssigneeFilterValue) => void;
  setReviewFilter: (v: ReviewFilterValue) => void;
}

/** Subscribe a component to the persisted view/filter prefs for one list. */
export function useListViewPrefs(listId: string): ListViewPrefsHandle {
  const map = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const stored = map[listId];
  const groupBy = clampGroupBy(stored?.groupBy);
  const assigneeFilter = stored?.assigneeFilter ?? DEFAULTS.assigneeFilter;
  const reviewFilter = clampReview(stored?.reviewFilter);

  const update = useCallback(
    (partial: Partial<ListViewPrefs>) => {
      const current = getSnapshot();
      commit({ ...current, [listId]: { ...current[listId], ...partial } });
    },
    [listId],
  );

  const setGroupBy = useCallback((v: GroupByValue) => update({ groupBy: v }), [update]);
  const setAssigneeFilter = useCallback(
    (v: AssigneeFilterValue) => update({ assigneeFilter: v }),
    [update],
  );
  const setReviewFilter = useCallback(
    (v: ReviewFilterValue) => update({ reviewFilter: v }),
    [update],
  );

  return { groupBy, assigneeFilter, reviewFilter, setGroupBy, setAssigneeFilter, setReviewFilter };
}
